import { useState, useEffect, useRef, useCallback } from 'react'
import { listTasks } from '../engine/TaskBuilder'
import { runAgent, getApiKey, type AgentMode } from '../engine/AgentBridge'
import { calculateMultiDimScore, type MultiDimScore } from '../engine/BenchmarkEngine'
import type { Task, TraceStep, Trace } from '../engine/types'
import {
  Play, Square, Clock, Zap, Activity, Loader, AlertTriangle, RefreshCw,
  CheckCircle, XCircle, ChevronRight, BarChart3, Target, Brain, Gauge,
  ShieldCheck, FileText, Wrench, Cpu, Link as LinkIcon
} from 'lucide-react'
import { useLang } from '../i18n/LangContext'
import { Link } from 'react-router-dom'

const stepTypeConfig: Record<string, { color: string; bg: string; label: string; icon: typeof Brain }> = {
  tool_call: { color: '#6c8cff', bg: 'rgba(108,140,255,0.12)', label: 'Tool Call', icon: Wrench },
  thinking: { color: '#c084fc', bg: 'rgba(192,132,252,0.12)', label: 'Thinking', icon: Brain },
  finish: { color: '#4ade80', bg: 'rgba(74,222,128,0.12)', label: 'Finish', icon: CheckCircle },
  error: { color: '#f87171', bg: 'rgba(248,113,113,0.12)', label: 'Error', icon: AlertTriangle },
}

export default function AgentRunnerPage() {
  const { t, lang } = useLang()
  const [tasks, setTasks] = useState<Task[]>([])
  const [tasksLoading, setTasksLoading] = useState(true)
  const [tasksError, setTasksError] = useState<string | null>(null)
  const [selectedTaskId, setSelectedTaskId] = useState('')
  const [agentName, setAgentName] = useState('DecisionCraft-Agent')
  const [agentMode, setAgentMode] = useState<AgentMode>('simulation')
  const [model, setModel] = useState('gpt-4o')
  const [maxSteps, setMaxSteps] = useState(25)
  const [timeout, setTimeout_] = useState(120)

  // Execution state
  const [running, setRunning] = useState(false)
  const [stopping, setStopping] = useState(false)
  const [steps, setSteps] = useState<TraceStep[]>([])
  const [progress, setProgress] = useState(0)
  const [runCompleted, setRunCompleted] = useState(false)
  const [runError, setRunError] = useState<string | null>(null)
  const [score, setScore] = useState<MultiDimScore | null>(null)
  const [finalTrace, setFinalTrace] = useState<Trace | null>(null)
  const [startTime, setStartTime] = useState<number | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const [expandedStep, setExpandedStep] = useState<number | null>(null)

  const stopRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [configError, setConfigError] = useState<string | null>(null)

  const fetchTasks = async () => {
    setTasksLoading(true)
    setTasksError(null)
    try {
      const data = await listTasks()
      setTasks(data)
      if (data.length > 0 && !selectedTaskId) {
        setSelectedTaskId(data[0].id)
      }
    } catch (err) {
      setTasksError(err instanceof Error ? err.message : 'Failed to load tasks')
    } finally {
      setTasksLoading(false)
    }
  }

  useEffect(() => {
    fetchTasks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  useEffect(() => {
    if (running && startTime) {
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTime) / 1000))
      }, 200)
      return () => {
        if (timerRef.current) clearInterval(timerRef.current)
      }
    }
  }, [running, startTime])

  const stopExecution = useCallback(() => {
    setStopping(true)
    stopRef.current = true
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    setRunning(false)
    setRunCompleted(true)
    setStopping(false)
  }, [])

  const handleRun = async () => {
    if (!selectedTaskId) {
      setConfigError(lang === 'zh' ? '请选择任务' : 'Please select a task')
      return
    }
    const task = tasks.find(t => t.id === selectedTaskId)
    if (!task) return

    setConfigError(null)
    setRunError(null)
    setRunCompleted(false)
    setScore(null)
    setFinalTrace(null)
    setSteps([])
    setProgress(0)
    setRunning(true)
    setStartTime(Date.now())
    setElapsed(0)
    setExpandedStep(null)
    stopRef.current = false

    const apiKey = agentMode === 'openai' ? getApiKey('openai') :
                   agentMode === 'anthropic' ? getApiKey('anthropic') : undefined

    if (agentMode === 'openai' && !apiKey) {
      setConfigError(lang === 'zh' ? '请先在设置页面配置OpenAI API Key' : 'Please configure OpenAI API key in Settings first')
      return
    }
    if (agentMode === 'anthropic' && !apiKey) {
      setConfigError(lang === 'zh' ? '请先在设置页面配置Anthropic API Key' : 'Please configure Anthropic API key in Settings first')
      return
    }

    try {
      await runAgent(task, {
        mode: agentMode,
        name: `${agentName} (${model})`,
        model,
        api_key: apiKey,
        max_steps: maxSteps,
        timeout_seconds: timeout,
      }, {
        onStep: (step) => {
          if (stopRef.current) return
          setSteps(prev => [...prev, step])
          setProgress(Math.round(((step.step_number) / (maxSteps + 2)) * 100))
        },
        onComplete: (trace) => {
          if (stopRef.current) return
          setFinalTrace(trace)
          const multiScore = calculateMultiDimScore(trace, task)
          setScore(multiScore)
          setProgress(100)
          setRunning(false)
          setRunCompleted(true)
          if (timerRef.current) clearInterval(timerRef.current)
        },
        onError: (error) => {
          setRunError(error)
          setRunning(false)
          setRunCompleted(true)
          if (timerRef.current) clearInterval(timerRef.current)
        },
      })
    } catch (err) {
      setRunError(err instanceof Error ? err.message : 'Execution failed')
      setRunning(false)
      setRunCompleted(true)
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }

  const selectedTask = tasks.find(t => t.id === selectedTaskId)

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  if (tasksLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader size={32} className="animate-spin" style={{ color: 'var(--color-accent)' }} />
          <p style={{ color: 'var(--color-text-secondary)' }}>{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  if (tasksError && tasks.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center px-4">
          <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(248,113,113,0.12)' }}>
            <AlertTriangle size={28} style={{ color: 'var(--color-danger)' }} />
          </div>
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Failed to load tasks</h3>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{tasksError}</p>
          <button
            onClick={fetchTasks}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
            style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
          >
            <RefreshCw size={15} /> Retry
          </button>
        </div>
      </div>
    )
  }

  const scoreDimensions = [
    { key: 'accuracy', label: lang === 'zh' ? '准确性' : 'Accuracy', max: 40, icon: Target, color: 'var(--color-accent)' },
    { key: 'process', label: lang === 'zh' ? '过程' : 'Process', max: 30, icon: Brain, color: '#c084fc' },
    { key: 'efficiency', label: lang === 'zh' ? '效率' : 'Efficiency', max: 20, icon: Gauge, color: 'var(--color-warning)' },
    { key: 'constraint', label: lang === 'zh' ? '约束' : 'Constraint', max: 10, icon: ShieldCheck, color: 'var(--color-success)' },
  ] as const

  return (
    <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{t('run.title')}</h1>
          <span
            className="px-2 py-0.5 rounded text-xs font-semibold"
            style={{
              backgroundColor: agentMode === 'simulation' ? 'rgba(251,191,36,0.12)' : 'rgba(74,222,128,0.12)',
              color: agentMode === 'simulation' ? 'var(--color-warning)' : 'var(--color-success)',
              border: `1px solid ${agentMode === 'simulation' ? 'rgba(251,191,36,0.25)' : 'rgba(74,222,128,0.25)'}`,
            }}
          >
            {agentMode === 'simulation'
              ? (lang === 'zh' ? '模拟运行' : 'Simulation Mode')
              : agentMode === 'openai'
                ? 'OpenAI API'
                : agentMode === 'anthropic'
                  ? 'Anthropic API'
                  : 'Webhook Agent'
            }
          </span>
        </div>
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          {agentMode === 'simulation'
            ? (lang === 'zh'
                ? '模拟运行模式：使用动态工具执行引擎生成轨迹，无需API Key。'
                : 'Simulation mode: generates traces using dynamic tool execution engine. No API key needed.')
            : agentMode === 'webhook'
              ? (lang === 'zh'
                  ? 'Webhook模式：外部Agent通过HTTP接收任务并调用工具执行。'
                  : 'Webhook mode: external agents receive tasks via HTTP and call tools for execution.')
              : (lang === 'zh'
                  ? '真实API模式：直接调用模型API，模型自主决定调用工具，记录完整决策轨迹。'
                  : 'Real API mode: calls model API directly, model autonomously decides tool calls, records full decision trace.')
          }
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Configuration Panel */}
        <div
          className="rounded-xl p-6"
          style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}
        >
          <h2 className="text-lg font-semibold mb-6 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <Zap size={18} style={{ color: 'var(--color-accent)' }} />
            {t('run.config')}
          </h2>

          {configError && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg mb-4 text-sm"
              style={{ backgroundColor: 'rgba(248,113,113,0.1)', color: 'var(--color-danger)', border: '1px solid rgba(248,113,113,0.25)' }}
            >
              <AlertTriangle size={14} />
              {configError}
            </div>
          )}

          <div className="space-y-5">
            {/* Task Selector */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                {t('run.task')}
              </label>
              <select
                value={selectedTaskId}
                onChange={e => setSelectedTaskId(e.target.value)}
                disabled={running}
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none appearance-none"
                style={{
                  backgroundColor: 'var(--color-bg-tertiary)',
                  color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-border)',
                  opacity: running ? 0.5 : 1,
                }}
              >
                {tasks.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.title} ({t.difficulty})
                  </option>
                ))}
              </select>
              {selectedTask && (
                <div className="mt-2 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                  <p className="text-xs whitespace-pre-line" style={{ color: 'var(--color-text-muted)' }}>
                    {selectedTask.description.slice(0, 200)}
                    {selectedTask.description.length > 200 ? '...' : ''}
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-[11px] px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-accent-glow)', color: 'var(--color-accent)' }}>
                      {selectedTask.difficulty}
                    </span>
                    <span className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
                      {lang === 'zh' ? '预期步骤' : 'Expected'}: {selectedTask.expected_steps}
                    </span>
                    <span className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
                      {lang === 'zh' ? '工具' : 'Tools'}: {selectedTask.tools_allowed.join(', ')}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Agent Mode Selector */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                {lang === 'zh' ? '运行模式' : 'Execution Mode'}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {([
                  { mode: 'simulation', label: lang === 'zh' ? '模拟运行' : 'Simulation', icon: Cpu },
                  { mode: 'openai', label: 'OpenAI API', icon: Cpu },
                  { mode: 'anthropic', label: 'Anthropic API', icon: Brain },
                  { mode: 'webhook', label: 'Webhook Agent', icon: LinkIcon },
                ] as const).map(({ mode, label, icon: Icon }) => (
                  <button
                    key={mode}
                    onClick={() => setAgentMode(mode)}
                    disabled={running}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all"
                    style={{
                      backgroundColor: agentMode === mode ? 'var(--color-accent-glow)' : 'var(--color-bg-tertiary)',
                      color: agentMode === mode ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                      border: `1px solid ${agentMode === mode ? 'var(--color-accent)' : 'var(--color-border)'}`,
                      opacity: running ? 0.5 : 1,
                    }}
                  >
                    <Icon size={14} />
                    {label}
                  </button>
                ))}
              </div>
              {/* API Key status for real modes */}
              {agentMode === 'openai' && (
                <div className="mt-2 flex items-center gap-2 text-xs">
                  {getApiKey('openai') ? (
                    <CheckCircle size={12} style={{ color: 'var(--color-success)' }} />
                  ) : (
                    <AlertTriangle size={12} style={{ color: 'var(--color-warning)' }} />
                  )}
                  <span style={{ color: getApiKey('openai') ? 'var(--color-success)' : 'var(--color-warning)' }}>
                    {getApiKey('openai')
                      ? (lang === 'zh' ? 'API Key 已配置' : 'API Key configured')
                      : (lang === 'zh' ? '未配置 — ' : 'Not configured — ')
                    }
                  </span>
                  {!getApiKey('openai') && (
                    <Link to="/settings" className="underline" style={{ color: 'var(--color-accent)' }}>
                      {lang === 'zh' ? '去设置' : 'Go to Settings'}
                    </Link>
                  )}
                </div>
              )}
              {agentMode === 'anthropic' && (
                <div className="mt-2 flex items-center gap-2 text-xs">
                  {getApiKey('anthropic') ? (
                    <CheckCircle size={12} style={{ color: 'var(--color-success)' }} />
                  ) : (
                    <AlertTriangle size={12} style={{ color: 'var(--color-warning)' }} />
                  )}
                  <span style={{ color: getApiKey('anthropic') ? 'var(--color-success)' : 'var(--color-warning)' }}>
                    {getApiKey('anthropic')
                      ? (lang === 'zh' ? 'API Key 已配置' : 'API Key configured')
                      : (lang === 'zh' ? '未配置 — ' : 'Not configured — ')
                    }
                  </span>
                  {!getApiKey('anthropic') && (
                    <Link to="/settings" className="underline" style={{ color: 'var(--color-accent)' }}>
                      {lang === 'zh' ? '去设置' : 'Go to Settings'}
                    </Link>
                  )}
                </div>
              )}
              {agentMode === 'webhook' && (
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <LinkIcon size={12} style={{ color: 'var(--color-text-muted)' }} />
                  <span style={{ color: 'var(--color-text-muted)' }}>
                    {lang === 'zh' ? '在设置页配置Webhook URL' : 'Configure Webhook URL in Settings'}
                  </span>
                  <Link to="/settings" className="underline" style={{ color: 'var(--color-accent)' }}>
                    {lang === 'zh' ? '去设置' : 'Settings'}
                  </Link>
                </div>
              )}
            </div>

            {/* Agent Name */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                {t('run.agentName')}
              </label>
              <input
                type="text"
                value={agentName}
                onChange={e => setAgentName(e.target.value)}
                disabled={running}
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                style={{
                  backgroundColor: 'var(--color-bg-tertiary)',
                  color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-border)',
                  opacity: running ? 0.5 : 1,
                }}
              />
            </div>

            {/* Model Selection */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                {t('run.model')}
              </label>
              <select
                value={model}
                onChange={e => setModel(e.target.value)}
                disabled={running}
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none appearance-none"
                style={{
                  backgroundColor: 'var(--color-bg-tertiary)',
                  color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-border)',
                  opacity: running ? 0.5 : 1,
                }}
              >
                {agentMode === 'simulation' && (
                  <>
                    <option value="gpt-4o">GPT-4o (Sim)</option>
                    <option value="claude-3.5-sonnet">Claude 3.5 (Sim)</option>
                    <option value="deepseek-v3">DeepSeek V3 (Sim)</option>
                    <option value="gemini-2.0-flash">Gemini 2.0 (Sim)</option>
                  </>
                )}
                {agentMode === 'openai' && (
                  <>
                    <option value="gpt-4o">GPT-4o</option>
                    <option value="gpt-4o-mini">GPT-4o Mini</option>
                    <option value="gpt-4-turbo">GPT-4 Turbo</option>
                    <option value="o1">o1</option>
                    <option value="o1-mini">o1-mini</option>
                  </>
                )}
                {agentMode === 'anthropic' && (
                  <>
                    <option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet</option>
                    <option value="claude-3-5-haiku-20241022">Claude 3.5 Haiku</option>
                    <option value="claude-3-opus-20240229">Claude 3 Opus</option>
                  </>
                )}
                {agentMode === 'webhook' && (
                  <option value="custom">Custom Agent</option>
                )}
              </select>
            </div>

            {/* Max Steps */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                {t('run.maxSteps')}: <span style={{ color: 'var(--color-accent)' }}>{maxSteps}</span>
              </label>
              <input
                type="range"
                min={5}
                max={100}
                step={5}
                value={maxSteps}
                onChange={e => setMaxSteps(Number(e.target.value))}
                disabled={running}
                className="w-full accent-[var(--color-accent)]"
                style={{ opacity: running ? 0.5 : 1 }}
              />
              <div className="flex justify-between mt-1">
                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>5</span>
                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>100</span>
              </div>
            </div>

            {/* Timeout */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                {t('run.timeout')}
              </label>
              <input
                type="number"
                min={10}
                max={600}
                value={timeout}
                onChange={e => setTimeout_(Number(e.target.value))}
                disabled={running}
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                style={{
                  backgroundColor: 'var(--color-bg-tertiary)',
                  color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-border)',
                  opacity: running ? 0.5 : 1,
                }}
              />
            </div>

            {/* Run Button */}
            <button
              onClick={handleRun}
              disabled={running || !selectedTaskId}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors"
              style={{
                backgroundColor: running ? 'var(--color-border)' : 'var(--color-accent)',
                color: '#fff',
                opacity: running || !selectedTaskId ? 0.5 : 1,
                cursor: running || !selectedTaskId ? 'not-allowed' : 'pointer',
              }}
            >
              <Play size={16} />
              {t('run.runAgent')}
            </button>
          </div>
        </div>

        {/* Right: Execution Panel */}
        <div
          className="rounded-xl p-6 flex flex-col"
          style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <Activity size={18} style={{ color: 'var(--color-accent)' }} />
              {t('run.execution')}
            </h2>
            {(running || stopping) && (
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  <Clock size={13} />
                  {formatTime(elapsed)}
                </span>
                <button
                  onClick={stopExecution}
                  disabled={stopping}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: 'rgba(248,113,113,0.12)',
                    color: 'var(--color-danger)',
                    opacity: stopping ? 0.5 : 1,
                  }}
                >
                  <Square size={12} />
                  {t('run.stop')}
                </button>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          {(running || runCompleted) && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  {running ? t('run.running') : t('run.complete')}
                </span>
                <span className="text-xs font-mono" style={{ color: 'var(--color-text-secondary)' }}>
                  {progress}%
                </span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${progress}%`,
                    backgroundColor: runCompleted ? 'var(--color-success)' : 'var(--color-accent)',
                  }}
                />
              </div>
            </div>
          )}

          {/* Steps Display */}
          <div className="flex-1 overflow-y-auto space-y-2 min-h-[300px] max-h-[500px] pr-1">
            {!running && !runCompleted && steps.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <Activity size={40} style={{ color: 'var(--color-text-muted)', margin: '0 auto' }} />
                <p className="mt-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  {t('run.placeholder')}
                </p>
              </div>
            )}

            {steps.map((step, i) => {
              const cfg = stepTypeConfig[step.action.type] ?? stepTypeConfig.error
              const Icon = cfg.icon
              const isExpanded = expandedStep === i
              const hasObservation = step.observation && step.action.type === 'tool_call'
              return (
                <div
                  key={i}
                  className="rounded-lg overflow-hidden transition-all"
                  style={{
                    backgroundColor: 'var(--color-bg-tertiary)',
                    borderLeft: `3px solid ${cfg.color}`,
                  }}
                >
                  {/* Step Header */}
                  <div
                    className={`px-3 py-2.5 ${hasObservation ? 'cursor-pointer' : ''}`}
                    onClick={() => hasObservation && setExpandedStep(isExpanded ? null : i)}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[11px] font-mono font-semibold" style={{ color: 'var(--color-text-muted)' }}>
                        #{step.step_number}
                      </span>
                      <span
                        className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase flex items-center gap-1"
                        style={{ backgroundColor: cfg.bg, color: cfg.color }}
                      >
                        <Icon size={10} />
                        {cfg.label}
                      </span>
                      {step.action.tool && (
                        <span className="text-[11px] font-medium" style={{ color: 'var(--color-accent)' }}>
                          {step.action.tool}
                        </span>
                      )}
                      {hasObservation && (
                        <ChevronRight
                          size={12}
                          className="ml-auto transition-transform"
                          style={{
                            color: 'var(--color-text-muted)',
                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          }}
                        />
                      )}
                      <span className="text-[10px] ml-auto" style={{ color: 'var(--color-text-muted)' }}>
                        {new Date(step.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    {step.action.reasoning && (
                      <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                        {step.action.reasoning}
                      </p>
                    )}
                    {step.action.args && step.action.type === 'tool_call' && (
                      <p className="text-[11px] mt-0.5 font-mono" style={{ color: 'var(--color-text-muted)' }}>
                        {lang === 'zh' ? '输入' : 'Input'}: {JSON.stringify(step.action.args).slice(0, 100)}
                      </p>
                    )}
                    {step.observation && (
                      <div className="flex items-center gap-1.5 mt-1">
                        {step.observation.status === 'success' ? (
                          <CheckCircle size={11} style={{ color: 'var(--color-success)' }} />
                        ) : (
                          <XCircle size={11} style={{ color: 'var(--color-danger)' }} />
                        )}
                        <span
                          className="text-[11px]"
                          style={{ color: step.observation.status === 'success' ? 'var(--color-success)' : 'var(--color-danger)' }}
                        >
                          {step.observation.status === 'success'
                            ? `${lang === 'zh' ? '成功' : 'Success'}${step.state_diff?.latency_ms ? ` (${step.state_diff.latency_ms}ms)` : ''}`
                            : `Error: ${step.observation.error?.message ?? 'Unknown'}`}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Expanded Observation Data */}
                  {isExpanded && step.observation?.data && (
                    <div
                      className="px-3 pb-3 pt-1 border-t"
                      style={{ borderColor: 'var(--color-border)' }}
                    >
                      <p className="text-[10px] font-semibold uppercase mb-1.5" style={{ color: 'var(--color-text-muted)' }}>
                        {lang === 'zh' ? '工具返回数据' : 'Tool Response Data'}
                      </p>
                      <pre
                        className="text-[11px] font-mono whitespace-pre-wrap break-all p-2 rounded"
                        style={{
                          backgroundColor: 'var(--color-bg-primary)',
                          color: 'var(--color-text-secondary)',
                          maxHeight: '200px',
                          overflow: 'auto',
                        }}
                      >
                        {JSON.stringify(step.observation.data, null, 2)}
                      </pre>
                    </div>
                  )}

                  {step.action.type === 'finish' && step.action.args && (
                    <p className="text-xs mt-0.5 px-3 pb-2" style={{ color: 'var(--color-success)' }}>
                      {step.action.args.summary}
                    </p>
                  )}
                </div>
              )
            })}

            {/* Multi-dimensional Score Card */}
            {runCompleted && score && (
              <div className="pt-4 space-y-4">
                {/* Score Summary */}
                <div
                  className="rounded-lg p-4"
                  style={{
                    backgroundColor: 'var(--color-accent-glow)',
                    border: '1px solid var(--color-accent)',
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-sm flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                      <BarChart3 size={16} style={{ color: 'var(--color-accent)' }} />
                      {lang === 'zh' ? '多维度评分' : 'Multi-Dimensional Score'}
                    </h3>
                    <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      {formatTime(elapsed)}
                    </span>
                  </div>

                  {/* Total Score */}
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold" style={{ color: 'var(--color-accent)' }}>
                      {score.total}
                    </span>
                    <span className="text-lg" style={{ color: 'var(--color-text-muted)' }}>/100</span>
                  </div>

                  {/* Dimension Scores */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {scoreDimensions.map(dim => {
                      const value = score[dim.key as keyof MultiDimScore] as number
                      const percentage = score.breakdown[dim.key as keyof typeof score.breakdown]
                      const DimIcon = dim.icon
                      return (
                        <div
                          key={dim.key}
                          className="rounded-lg p-3"
                          style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)' }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <DimIcon size={14} style={{ color: dim.color }} />
                            <span className="text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                              {dim.label}
                            </span>
                            <span className="text-xs font-mono ml-auto" style={{ color: dim.color }}>
                              {value}/{dim.max}
                            </span>
                          </div>
                          <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${percentage}%`, backgroundColor: dim.color }}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Evidence */}
                  {score.evidence.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-[10px] font-semibold uppercase" style={{ color: 'var(--color-text-muted)' }}>
                        {lang === 'zh' ? '评分依据' : 'Evidence'}
                      </p>
                      {score.evidence.map((ev, idx) => (
                        <div key={idx} className="flex items-start gap-1.5">
                          <CheckCircle size={10} className="mt-0.5 shrink-0" style={{ color: 'var(--color-success)' }} />
                          <span className="text-[11px]" style={{ color: 'var(--color-text-secondary)' }}>{ev}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Final Answer Summary */}
                {finalTrace?.final_answer && (
                  <div
                    className="rounded-lg p-4"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)' }}
                  >
                    <h3 className="font-semibold text-sm flex items-center gap-2 mb-3" style={{ color: 'var(--color-text-primary)' }}>
                      <FileText size={16} style={{ color: 'var(--color-info)' }} />
                      {lang === 'zh' ? '最终方案' : 'Final Answer'}
                    </h3>
                    <pre
                      className="text-[11px] font-mono whitespace-pre-wrap break-all p-3 rounded"
                      style={{
                        backgroundColor: 'var(--color-bg-primary)',
                        color: 'var(--color-text-secondary)',
                        maxHeight: '300px',
                        overflow: 'auto',
                      }}
                    >
                      {JSON.stringify(finalTrace.final_answer, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Execution Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center rounded-lg p-3" style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)' }}>
                    <p className="text-2xl font-bold" style={{ color: 'var(--color-accent)' }}>
                      {steps.filter(s => s.action.type === 'tool_call').length}
                    </p>
                    <p className="text-[11px] mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                      {lang === 'zh' ? '工具调用' : 'Tool Calls'}
                    </p>
                  </div>
                  <div className="text-center rounded-lg p-3" style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)' }}>
                    <p className="text-2xl font-bold" style={{ color: 'var(--color-success)' }}>
                      {steps.filter(s => s.observation?.status === 'success').length}
                    </p>
                    <p className="text-[11px] mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                      {lang === 'zh' ? '成功' : 'Success'}
                    </p>
                  </div>
                  <div className="text-center rounded-lg p-3" style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)' }}>
                    <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                      {steps.length}
                    </p>
                    <p className="text-[11px] mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                      {lang === 'zh' ? '总步骤' : 'Total Steps'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {runError && (
              <div
                className="flex items-center gap-2 text-sm p-3 rounded-lg mt-2"
                style={{ backgroundColor: 'rgba(248,113,113,0.12)', color: 'var(--color-danger)' }}
              >
                <AlertTriangle size={15} />
                {runError}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
